import React, { useMemo, useState } from 'react';
import { Button, Card, Input, message, Modal, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import { j5n8Api, J5N8PendingItem } from '../../services/j5n8Api';
import { BatchAcceptanceBridge, pwj5n8f1AcceptanceManifest } from '../../features/acceptance';

const { Title, Text } = Typography;
const rowKey = (item: J5N8PendingItem) => `${item.form_id}:${item.doc_no}`;

const J5N8Reversal: React.FC = () => {
  const [docStart, setDocStart] = useState('');
  const [docEnd, setDocEnd] = useState('');
  const [items, setItems] = useState<J5N8PendingItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [reversing, setReversing] = useState(false);

  const selectedItems = useMemo(
    () => items.filter((item) => selectedRowKeys.includes(rowKey(item))),
    [items, selectedRowKeys],
  );

  const fetchPending = async () => {
    if (!docStart.trim() || !docEnd.trim()) {
      message.warning('請輸入起訖分析單號');
      return;
    }
    setLoading(true);
    setSelectedRowKeys([]);
    try {
      const response = await j5n8Api.pending(docStart.trim(), docEnd.trim());
      setItems(response.items || []);
    } catch (error: any) {
      message.error(error?.response?.data?.error || '載入可沖銷資料失敗');
    } finally {
      setLoading(false);
    }
  };

  const confirmReverse = () => {
    if (selectedItems.length === 0) {
      message.warning('請至少選擇一筆已核准資料');
      return;
    }
    Modal.confirm({
      title: '確認核准沖銷',
      content: `將 ${selectedItems.length} 筆已核准資料由 P 轉為 N，是否繼續？`,
      okText: '確認沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        setReversing(true);
        try {
          await j5n8Api.reverse(selectedItems.map(({ form_id, doc_no }) => ({ form_id, doc_no })));
          message.success(`已沖銷 ${selectedItems.length} 筆`);
          await fetchPending();
        } catch (error: any) {
          message.error(error?.response?.data?.error || '核准沖銷失敗');
        } finally {
          setReversing(false);
        }
      },
    });
  };

  const columns: ColumnsType<J5N8PendingItem> = [
    { title: '來源', dataIndex: 'form_id', width: 110 },
    { title: '分析單號', dataIndex: 'doc_no', width: 110, render: (value) => <Text code>{value}</Text> },
    { title: '開發單號', dataIndex: 'development_no', width: 110 },
    { title: '入單日期', dataIndex: 'entry_date', width: 110 },
    { title: '產品代號', dataIndex: 'product_code', width: 160 },
    { title: '單據類別', dataIndex: 'document_type', width: 130 },
    { title: '狀態', dataIndex: 'status', width: 70, render: () => <Tag color="blue">P</Tag> },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Title level={4}>新產品分析核准沖銷（pwj5n8f1）</Title>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <label htmlFor="doc_no_start">分析單號（起）</label>
          <Input id="doc_no_start" data-contract-field="doc_no_start" value={docStart} onChange={(event) => setDocStart(event.target.value)} placeholder="起始分析單號" />
          <label htmlFor="doc_no_end">分析單號（迄）</label>
          <Input id="doc_no_end" data-contract-field="doc_no_end" value={docEnd} onChange={(event) => setDocEnd(event.target.value)} placeholder="截止分析單號" />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={fetchPending}>查詢</Button>
          <Button danger icon={<RollbackOutlined />} loading={reversing} disabled={selectedItems.length === 0} onClick={confirmReverse}>
            沖銷已選 {selectedItems.length} 筆
          </Button>
        </Space>
      </Card>
      <Table<J5N8PendingItem>
        rowKey={rowKey}
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        scroll={{ x: 900 }}
      />
      <BatchAcceptanceBridge
        manifest={pwj5n8f1AcceptanceManifest}
        records={items}
        selectedKeys={selectedItems.map(rowKey)}
        keyOf={rowKey}
        queryExact={async (key) => {
          const docNo = key.slice(key.indexOf(':') + 1);
          return j5n8Api.pending(docNo, docNo);
        }}
        lastApiResponse={items}
        loading={loading || reversing}
        onAutoFill={() => items[0] && setSelectedRowKeys([rowKey(items[0])])}
        autoFillDisabled={items.length === 0}
        screenDraft={{ docStart, docEnd, selected: selectedItems }}
      />
    </div>
  );
};

export default J5N8Reversal;
